/**
 * parkingScaleMetrics
 *
 * 그래프마다 좌표 스케일이 다르다 — 신규 CSV 렌더(도면 스케일 슬라이더 기준)는
 * 주차면 단변이 수십 px 인 반면, 저장 후 재진입한 그래프는 수백 px(혹은
 * normalize 된 m 단위)일 수 있다. 고정 크기(폰트 4, 선 0.75)로 그리던 라벨/
 * 관계선 오버레이가 재진입 그래프에서 "엄청 작게" 보이던 원인.
 *
 * 일반 주차구역(단변)의 중앙값을 그래프의 기준 길이로 삼아 폰트 크기와
 * 관계선 굵기를 비례 산출한다. 주차면이 없는 그래프는 기존 고정값으로 폴백.
 */

import { dia } from '@joint/core'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import { LABEL_FONT_SIZE } from './renderObjectLabelNode'

/**
 * 기준 비율: 신규 CSV 렌더(주차면 단변 ≈ 27px)에서 기존 고정값(폰트 4)과
 * 동일하게 보이도록 4/27 ≈ 0.15 로 잡았다. 재진입 그래프(단변 ≈ 200px)에선
 * 폰트 ≈ 30 으로 주차면 대비 동일한 비율을 유지한다.
 */
const FONT_SIZE_TO_PARKING_SHORT_RATIO = 0.15

/** 주차면 폴리곤 기본 선 굵기 (csvRenderer typeStrokeWidth 기본값과 동일). */
const DEFAULT_PARKING_STROKE_WIDTH = 2

/** 해당 엘리먼트가 주차면(parkingLocation 카테고리)인지 판별. */
function isParkingElement(element: dia.Element): boolean {
  const layer = element.get('data')?.layer
  if (typeof layer !== 'string' || layer.length === 0) return false
  return routeLayerToCategory(layer) === 'parkingLocation'
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

/** 그래프 내 일반 주차면 단변 길이의 중앙값. 주차면이 없으면 null. */
export function medianParkingShortSide(graph: dia.Graph): number | null {
  const shorts: number[] = []
  for (const element of graph.getElements()) {
    if (!isParkingElement(element)) continue
    const { width, height } = element.size()
    const short = Math.min(width, height)
    if (short > 0) shorts.push(short)
  }
  return median(shorts)
}

/**
 * 객체 라벨(ID/IP/이름) 폰트 크기 — 주차면 단변에 비례.
 * 주차면이 없으면 기존 고정값(LABEL_FONT_SIZE) 유지.
 */
export function computeLabelFontSize(graph: dia.Graph | null): number {
  if (!graph) return LABEL_FONT_SIZE
  const short = medianParkingShortSide(graph)
  if (!short) return LABEL_FONT_SIZE
  return Math.max(LABEL_FONT_SIZE, short * FONT_SIZE_TO_PARKING_SHORT_RATIO)
}

/**
 * 관계선 굵기 — 주차구역 외곽선과 비슷하게. 주차면 엘리먼트의 실제
 * body/strokeWidth 중앙값을 그대로 쓰고, 없으면 기본 2.
 */
export function computeRelationStrokeWidth(graph: dia.Graph | null): number {
  if (!graph) return DEFAULT_PARKING_STROKE_WIDTH
  const widths: number[] = []
  for (const element of graph.getElements()) {
    if (!isParkingElement(element)) continue
    const sw = Number(element.attr('body/strokeWidth'))
    if (Number.isFinite(sw) && sw > 0) widths.push(sw)
  }
  return median(widths) ?? DEFAULT_PARKING_STROKE_WIDTH
}
